import React, { useState, useEffect } from "react";
import Breadcrumb from "../../../components/Breadcrumb";
import Grid from "../../../components/Grid";
import AddonPacksService from "../../../Api/AddonPacksService";
import { useNavigate } from "react-router-dom";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2/dist/sweetalert2.js';

const columns = [
    { 
      header: 'Pack Name',
      key: 'pack_name',
      render: (item) => (
        <div className="d-flex align-items-center">
          <img
            src={item.thumbnail_url || 'assets/images/default-pack.png'}
            alt={item.pack_name}
            className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden"
          />
          <div className="flex-grow-1">
            <span className="text-md mb-0 fw-normal text-secondary-light">
              {item.pack_name}
            </span>
          </div>
        </div>
      )
    },
    {
      header: 'Price',
      key: 'price',
      render: (item) => (
        <span>₹{item.price}</span>
      )
    },
    {
      header: 'Points Provided',
      key: 'points_provided'
    },
    {
      header: 'Status',
      key: 'is_active',
      center: true,
      render: (item) => (
        <span className={`${item.is_active ? 'bg-success-focus text-success-600 border border-success-main' : 'bg-neutral-200 text-neutral-600 border border-neutral-400'} px-24 py-4 radius-4 fw-medium text-sm`}>
          {item.is_active ? 'Active' : 'Inactive'}
        </span>
      )
    }
];

const AddonPacksList = () => {
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const packAPI = new AddonPacksService();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchPacks = async () => {
    try {
      setLoading(true);
      const response = await packAPI.GetAllAddonPacks();
      if (response.data && response.data.data) {
        setPacks(response.data.data);
        toast.success('Addon packs loaded successfully!');
      }
    } catch (err) {
      console.error("Error fetching addon packs:", err);
      toast.error("Failed to load addon packs. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPacks();
  }, []);

  const handleItemsPerPage = (perPage) => {
    setItemsPerPage(perPage);
    setCurrentPage(1);
  };

  const handleEdit = (pack) => {
    navigate(`/add-addon-pack?id=${pack.id}`);
  };

  const handleView = (pack) => {
    navigate(`/view-addon-pack?id=${pack.id}`);
  };

  const handleDelete = async (pack) => {
    try {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
        showLoaderOnConfirm: true,
        preConfirm: async () => {
          try {
            const response = await packAPI.DeleteById(pack.id);
            if (response.data && response.data.status === "success") {
              return true;
            } else {
              throw new Error(response.data?.message || 'Failed to delete addon pack');
            }
          } catch (error) {
            Swal.showValidationMessage(
              `Delete failed: ${error.response?.data?.message || error.message}`
            );
          }
        },
        allowOutsideClick: () => !Swal.isLoading()
      });

      if (result.isConfirmed) {
        await Swal.fire({
          title: 'Deleted!',
          text: 'Addon pack has been deleted.',
          icon: 'success',
          timer: 1500,
          showConfirmButton: false
        });
        toast.success('Addon pack deleted successfully!');
        fetchPacks();
      }
    } catch (error) {
      console.error("Error in delete operation:", error);
      toast.error('An unexpected error occurred');
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <Breadcrumb title="Addon Packs List" />
      
      <Grid 
        data={packs}
        columns={columns}
        add="add-addon-pack"
        onEdit={handleEdit}
        onDelete={handleDelete}
        onView={handleView}
        onItemsPerPageChange={handleItemsPerPage}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        itemsPerPage={itemsPerPage}
        showSearch={false}
        showStatusFilter={false}
      />
    </>
  );
};

export default AddonPacksList;

